import axios, { type AxiosInstance } from 'axios';
import toast from 'react-hot-toast';
import { useEffect, useState } from 'react';

export const hubApi = axios.create({
  baseURL: '/api/hub',
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

export const internalApi = axios.create({
  baseURL: '/api/internal',
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

export const authApi = axios.create({
  baseURL: '/api/auth',
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

// Session-expiry interceptor. Today's sessions are in-memory (Express default),
// so any server restart wipes them. Without this interceptor, pages that
// fetch from the API on mount (Users & Permissions, Data Health) just sit on
// a raw "Not connected to a Salesforce org" error and the user has no way
// forward. We catch the 401 once, surface a friendly toast, then bounce
// them to the appropriate login page (client-admins → /hub-login,
// consultants → /connect). The redirect uses a guard so a burst of 401s
// in-flight only triggers one navigation.
let redirecting = false;
function installSessionExpiryHandler(api: AxiosInstance, defaultTarget: string) {
  api.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401 && !redirecting) {
        redirecting = true;
        toast.error('Your session has expired. Reconnect to continue.');
        // If the user is currently inside the /hub route tree, send them
        // to the client-facing login; otherwise use the api-instance's
        // default target. window.location (not react-router navigate) so
        // the app re-bootstraps cleanly with the fresh login state.
        const target = window.location.pathname.startsWith('/hub')
          ? '/hub-login'
          : defaultTarget;
        setTimeout(() => {
          window.location.href = target;
        }, 600);
      }
      return Promise.reject(err);
    },
  );
}
installSessionExpiryHandler(hubApi, '/hub-login');
installSessionExpiryHandler(internalApi, '/connect');

export type Me = {
  // null when the session holds no role: not a consultant, not a client admin.
  role: 'consultant' | 'client-admin' | null;
  sfOrgId: string | null;
  sfUserId: string | null;
  email: string | null;
  username: string | null;
};

export async function fetchMe(): Promise<Me | null> {
  try {
    const { data } = await authApi.get<Me>('/me');
    return data;
  } catch {
    return null;
  }
}

// ── Balo booking link (TYR-227) ──────────────────────────────────────────
// The Hub reads the link from the server once per page load. Empty = not
// configured or not https, and the Hub then shows no button and no card.
let baloUrlPromise: Promise<string> | null = null;
function fetchBaloUrl(): Promise<string> {
  baloUrlPromise ??= hubApi
    .get<{ url: string }>('/balo')
    .then((r) => (typeof r.data?.url === 'string' && r.data.url.startsWith('https://') ? r.data.url : ''))
    .catch(() => '');
  return baloUrlPromise;
}

export function useBaloUrl(): string {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let live = true;
    void fetchBaloUrl().then((u) => {
      if (live) setUrl(u);
    });
    return () => {
      live = false;
    };
  }, []);
  return url;
}

// Records a booking link click. Fire and forget: the caller is a plain
// target="_blank" link, so the browser opens the tab itself (no popup
// blocker) whatever happens here. Plain fetch, not axios, so a 401 never
// triggers the session-expiry redirect on a click.
export function recordBaloClick(path: '/api/hub/balo-click' | '/api/prospect/balo-click'): void {
  try {
    void fetch(path, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => {});
  } catch {
    // Never block the link.
  }
}
